import { Form } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { candidateStatusLabel } from "@/components/candidates/candidate-status-badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useIsMobile } from "@/hooks/use-mobile"
import { useOnline } from "@/hooks/use-online"
import type { CandidateStatus } from "@/types/candidates"

/**
 * OKS-19 F8 — Revert confirmation modal.
 *
 * OKS-948 — the destination is no longer fixed. `revert` now covers three
 * cases (`approved → reviewed`, `contacted → approved`, and `rejected → the
 * status it was rejected from`), so the old "Move back to reviewed" copy was
 * false for four of the five paths. The dialog now NAMES the destination from
 * the server-supplied `destination` prop.
 *
 * **Per-state availability** (UX Spec §Per-State Action Bar — joint
 * freeze): the Revert button renders only when `actions.revert_url`
 * is non-null, which BE serializes when `current_status ∈ {approved,
 * rejected}`. PRD §Status Pipeline: only `approved → reviewed` and
 * `rejected → reviewed` get this affordance in V1; other backward
 * steps explicitly out-of-scope.
 *
 * **Wire format (BE TRD lock — single revert event covers both source
 * states):** `event: "revert"` is the same literal wire string for
 * both `approved → reviewed` and `rejected → reviewed`. BE's service
 * decodes the from-status from the candidate's current status. FE
 * never inspects the source state.
 *
 *   POST /candidates/:id/transitions
 *   body: { transition: { event: "revert" } }
 *
 * **No reason field** (UX Spec §Modals — joint freeze with ux-oks19):
 * revert is corrective, not destructive. The user is undoing their
 * own decision; no reason needed. Distinct from blocklist (required
 * reason) and reject (optional reason).
 *
 * **shadcn primitives:** `Drawer` on `<640px`, `Dialog` on `≥640px` —
 * matches the OKS-18 reject-modal + F5 bulk-reject + F9
 * blocklist/unblocklist patterns. Focus trap + ESC + restored focus
 * to the Revert button on close (parent owns the trigger via
 * `onOpenChange`).
 *
 * **Locked testids (FE TRD §testid Map):**
 *   - `candidate-detail-revert-confirm-dialog` — modal root
 *   - `candidate-detail-revert-confirm` — destructive-corrective submit
 *   - `candidate-detail-revert-cancel` — ghost cancel
 *
 * The trigger button (`candidate-detail-revert`) lives in
 * `CandidateActionBar` (F7); this component owns the modal only.
 */
export interface RevertConfirmationProps {
  /** Controlled open state — parent (Show.tsx) owns the modal lifecycle. */
  open: boolean
  /** Close handler — fires on ESC, overlay click, or Cancel button. */
  onOpenChange: (open: boolean) => void
  /**
   * BE-supplied transition endpoint URL — `actions.revert_url`.
   * Caller is responsible for guarding against null at mount-time
   * (the modal accepts only string here; null URL → no modal mounted).
   */
  actionUrl: string
  /**
   * OKS-948 — where this candidate will land, server-resolved by
   * `Candidate#revert_destination` and shipped as the `revert_destination`
   * prop. Interpolated into the dialog description so the user is told the
   * destination BEFORE confirming.
   *
   * ⚠️ Never re-derive this from the candidate's status on the client. The
   * `rejected` case reads the status off the latest `reject` audit event,
   * which the client cannot see — and the whole point of the prop is that
   * the copy and the transition resolve through the SAME code path.
   */
  destination: CandidateStatus
  /**
   * Optional finished-callback fired after the Inertia POST resolves.
   * Show.tsx uses this defensively to close the modal even though the
   * server 303-redirects (the page re-mounts with fresh props anyway).
   */
  onSuccess?: () => void
  /**
   * **OKS-950 (R4)** — `appeal_state` from the Show props.
   *
   * `"available"` turns this dialog into the APPEAL: it gains a REQUIRED
   * argumentaire textarea and the sentence saying this is the agency's one
   * reply. Any other value leaves the shipped confirmation untouched — a
   * `revert` out of `contacted`/`approved`, or out of a screening rejection,
   * is not an appeal and stays note-optional.
   *
   * ⚠️ Driven by `appeal_state`, NOT by `destination === "pending_brand_validation"`.
   * The two coincide today, but the destination says WHERE the candidate
   * lands while only `appeal_state` says whether an appeal is being SPENT —
   * and it is the spend, not the landing place, that makes the note required.
   * `Candidates::Transition` enforces the same rule server-side
   * (`:appeal_note_required`), so a divergence surfaces as an inline field
   * error rather than as a silently dropped note.
   */
  appealState?: "available" | "spent" | null
}

export function RevertConfirmation({
  open,
  onOpenChange,
  actionUrl,
  destination,
  onSuccess,
  appealState = null,
}: RevertConfirmationProps) {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const online = useOnline()

  // Resolve the destination's LABEL through the shared status-label switch
  // so the `approved` → "To contact" relabel is inherited automatically and
  // no fifth exhaustive switch is introduced.
  const destinationLabel = candidateStatusLabel(t, destination)
  const description = t("candidates.detail.actions.revert_dialog_description", {
    status: destinationLabel,
  })

  const formContent = (
    <Form
      action={actionUrl}
      method="post"
      options={{ preserveScroll: true }}
      onSuccess={() => onSuccess?.()}
    >
      {({ errors, processing }) => (
        <>
          {/*
            Hidden `transition[event]` carries the literal "revert"
            wire string — BE's service decodes the from-status. Vitest
            literal-name canary asserts this exact attribute value
            via `name=` selector (not testid).
          */}
          <input type="hidden" name="transition[event]" value="revert" />
          {/*
            OKS-950 (R4) — the appeal argumentaire. Rendered ONLY when an
            appeal is actually available, so the shipped `contacted`/`approved`
            reverts keep their one-click confirmation.

            `required` + `aria-required` are the browser-side half; the
            authority is `Candidates::Transition`, which returns
            `:appeal_note_required` and lands as an INLINE field error on
            `reason` (UX Spec §3: an empty appeal note is a field error, not a
            flash). Both halves exist because the client half is a courtesy
            and the server half is the rule.
          */}
          {appealState === "available" && (
            <div className="grid gap-2 py-2">
              <Label htmlFor="candidate-detail-appeal-note">
                {t("candidates.detail.actions.appeal_note_label")}
                <span className="text-destructive ml-0.5" aria-hidden="true">
                  *
                </span>
              </Label>
              {/*
                ⚠️ NO native `required` — Crew posture (OKS-661/OKS-692), and
                here it would defeat the feature rather than merely break a
                convention. Native `required` makes the BROWSER block submit
                with its own non-localized message, in an app that renders
                French. Worse, the request would never reach the server, so
                `Candidates::Transition`'s `:appeal_note_required` path would
                never run and the empty-appeal case would never produce the
                inline field error UX Spec §3 requires — the walk step PO
                reinstated exists precisely to catch that.

                The `*` marker above carries the requirement visually,
                `aria-required` carries it to assistive tech, and the SERVER
                is the enforcement point. Same shape as
                `blocklist-confirmation.tsx`, the compliant sibling in this
                dialog family.
              */}
              <Textarea
                id="candidate-detail-appeal-note"
                data-testid="candidate-detail-appeal-note"
                name="transition[reason]"
                rows={4}
                aria-required="true"
                aria-invalid={errors.reason ? "true" : undefined}
                aria-describedby={
                  errors.reason
                    ? "candidate-detail-appeal-note-error"
                    : "candidate-detail-appeal-warning"
                }
                placeholder={t(
                  "candidates.detail.actions.appeal_note_placeholder",
                )}
                disabled={processing}
              />
              {/*
                The server's `:appeal_note_required`, rendered inline and
                localized — UX Spec §3: an empty appeal note is a FIELD error,
                never a flash. `role="alert"` announces it immediately. Same
                element as the blocklist dialog's.
              */}
              {errors.reason && (
                <p
                  id="candidate-detail-appeal-note-error"
                  data-testid="candidate-detail-appeal-note-error"
                  role="alert"
                  className="text-destructive text-xs"
                >
                  {errors.reason}
                </p>
              )}
              <p
                id="candidate-detail-appeal-warning"
                data-testid="candidate-detail-appeal-warning"
                className="text-muted-foreground text-sm"
              >
                {t("candidates.detail.actions.appeal_warning")}
              </p>
            </div>
          )}
          {isMobile ? (
            <DrawerFooter className="px-0">
              <Button
                type="submit"
                variant="default"
                data-testid="candidate-detail-revert-confirm"
                disabled={processing || !online}
              >
                {t("candidates.detail.actions.revert_confirm")}
              </Button>
              <Button
                type="button"
                variant="ghost"
                data-testid="candidate-detail-revert-cancel"
                disabled={processing}
                onClick={() => onOpenChange(false)}
              >
                {t("candidates.detail.actions.revert_cancel")}
              </Button>
            </DrawerFooter>
          ) : (
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                data-testid="candidate-detail-revert-cancel"
                disabled={processing}
                onClick={() => onOpenChange(false)}
              >
                {t("candidates.detail.actions.revert_cancel")}
              </Button>
              <Button
                type="submit"
                variant="default"
                data-testid="candidate-detail-revert-confirm"
                disabled={processing || !online}
              >
                {t("candidates.detail.actions.revert_confirm")}
              </Button>
            </DialogFooter>
          )}
        </>
      )}
    </Form>
  )

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent data-testid="candidate-detail-revert-confirm-dialog">
          <DrawerHeader>
            <DrawerTitle>
              {t("candidates.detail.actions.revert_dialog_title")}
            </DrawerTitle>
            <DrawerDescription>{description}</DrawerDescription>
          </DrawerHeader>
          <div className="px-4 pb-4">{formContent}</div>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="candidate-detail-revert-confirm-dialog"
        className="sm:max-w-[480px]"
      >
        <DialogHeader>
          <DialogTitle>
            {t("candidates.detail.actions.revert_dialog_title")}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {formContent}
      </DialogContent>
    </Dialog>
  )
}
